"use client";

import { Check, Puzzle } from "lucide-react";
import { useRef, useState } from "react";
import { api } from "@/lib/api";
import ActivityFrame from "./ActivityFrame";

export interface MatchEntry {
  id: string;
  text: string;
}

/**
 * Lefts arrive in order, rights arrive shuffled — both with opaque ids, so
 * the pairings never reach the client. Each guess is checked server-side via
 * /check-matching. Entries are keyed by id (duplicate display text is fine).
 */
export default function MatchingActivity({
  courseId,
  interactiveId,
  title,
  lefts,
  rights,
  onComplete,
  checkMatch: checkOverride,
}: {
  courseId?: string;
  interactiveId?: string;
  title: string;
  lefts: MatchEntry[];
  rights: MatchEntry[];
  // Called once when every pair is matched
  onComplete?: () => void;
  // Editor preview of UNSAVED drafts supplies a local checker (the
  // instructor already knows the answers); students always hit the server.
  checkMatch?: (leftId: string, rightId: string) => Promise<{ correct: boolean }>;
}) {
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  // left id -> right id, in match order (insertion order drives the badges)
  const [matches, setMatches] = useState<Record<string, string>>({});
  const [misses, setMisses] = useState(0);
  const [wrong, setWrong] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checkError, setCheckError] = useState(false);
  // Live-region text so right/wrong is announced, not just colored
  const [announce, setAnnounce] = useState("");
  const completed = useRef(false);

  const matchedRights = new Set(Object.values(matches));
  const matchedLefts = Object.keys(matches);
  const done = matchedLefts.length === lefts.length && lefts.length > 0;
  const numberForLeft = (leftId: string) => matchedLefts.indexOf(leftId) + 1;
  const numberForRight = (rightId: string) => {
    const left = matchedLefts.find((l) => matches[l] === rightId);
    return left ? numberForLeft(left) : 0;
  };
  const textOf = (entries: MatchEntry[], id: string) =>
    entries.find((e) => e.id === id)?.text ?? "";

  const tryMatch = async (rightId: string) => {
    if (!selectedLeft || busy) return;
    setBusy(true);
    setCheckError(false);
    try {
      const result = checkOverride
        ? await checkOverride(selectedLeft, rightId)
        : await api<{ correct: boolean }>(
            `/api/v1/courses/${courseId}/interactives/${interactiveId}/check-matching`,
            {
              method: "POST",
              body: JSON.stringify({ left_id: selectedLeft, right_id: rightId }),
            },
          );
      if (result.correct) {
        const next = { ...matches, [selectedLeft]: rightId };
        setMatches(next);
        setSelectedLeft(null);
        setAnnounce(
          `Matched "${textOf(lefts, selectedLeft)}" with "${textOf(rights, rightId)}".`,
        );
        if (Object.keys(next).length === lefts.length && !completed.current) {
          completed.current = true;
          onComplete?.();
        }
      } else {
        setMisses((m) => m + 1);
        setWrong(rightId);
        setAnnounce("Not a match — try another.");
        setTimeout(() => setWrong(null), 350);
      }
    } catch {
      setCheckError(true);
    } finally {
      setBusy(false);
    }
  };

  // A small numbered badge linking a matched left item to its right item.
  const PairBadge = ({ n }: { n: number }) => (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--la-soft)] text-[11px] font-bold text-[var(--la-ink)]">
      {n}
    </span>
  );

  return (
    <ActivityFrame
      type="matching"
      kindLabel="Matching"
      icon={Puzzle}
      title={title}
      meta={
        done ? (
          <span className="activity__done" role="status">
            <Check className="h-3.5 w-3.5" /> {misses === 0 ? "Perfect!" : `${misses} misses`}
          </span>
        ) : (
          `${matchedLefts.length} / ${lefts.length}`
        )
      }
    >
      <div className="grid grid-cols-1 gap-2.5 min-[440px]:grid-cols-2">
        <div className="space-y-2">
          {lefts.map((left) => {
            const matched = left.id in matches;
            const active = selectedLeft === left.id;
            return (
              <button
                key={left.id}
                disabled={matched}
                onClick={() => setSelectedLeft(left.id)}
                className={`match-pill ${matched ? "is-matched" : ""} ${active ? "is-active" : ""}`}
              >
                {matched && <PairBadge n={numberForLeft(left.id)} />}
                {left.text}
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          {rights.map((right) => {
            const matched = matchedRights.has(right.id);
            return (
              <button
                key={right.id}
                disabled={matched || !selectedLeft || busy}
                onClick={() => tryMatch(right.id)}
                className={`match-pill ${matched ? "is-matched" : ""} ${wrong === right.id ? "is-wrong" : ""}`}
              >
                {matched && <PairBadge n={numberForRight(right.id)} />}
                {right.text}
              </button>
            );
          })}
        </div>
      </div>
      <p aria-live="polite" className="sr-only">
        {announce}
      </p>
      {checkError && (
        <p className="mt-2.5 text-center text-xs text-danger" role="status">
          Couldn&apos;t check that match right now — try again.
        </p>
      )}
      {!done && !checkError && (
        <p className="mt-2.5 text-center text-xs text-muted">
          {selectedLeft ? "Now pick its match on the right" : "Pick an item on the left"}
        </p>
      )}
    </ActivityFrame>
  );
}
